import { useEffect } from "react";
import { useActiveUploads, useCrossDeviceUploads, usePackagesActions } from "@/store/packagesSlice";
import { shouldFastPollFromState } from "./shouldFastPollFromState";

// Boot-time + interaction-driven watcher for the contributor's cross-device upload set. This
// is the counterpart to ResumePreviousUploadBanner (which reads IndexedDB) — /sessions/active
// is the server's view, which lets a fresh browser (no IDB) see uploads owned by another
// device and lets any device confirm ownership after a claim.
//
// The component renders nothing; consumers subscribe via `useCrossDeviceUploads()`.
//
// Polling strategy is adaptive:
//   - Baseline: one initial fetch on mount + on every window focus / tab visibility wake.
//     Costs nothing when the user is idle in another window.
//   - Fast poll (FAST_INTERVAL_MS): starts only while a remote upload is visible in the
//     store, and stops the moment the remote set becomes empty. This surfaces "B just finished
//     — hide the card" within a couple of seconds without an always-on timer. If a new remote
//     upload appears while the fast timer is off, we catch it on the next focus/visibility
//     event (which is when the user would notice anyway).

const FAST_INTERVAL_MS = 2 * 1000;

export const CrossDeviceUploadsWatcher = () => {
  const { fetchActiveSessions } = usePackagesActions();
  const { items } = useCrossDeviceUploads();
  const activeUploads = useActiveUploads();
  // Fast-poll while ANY local upload card is visible — UPLOADING (live or processing on
  // backend after takeover reset it), PAUSED (rehydrated after reload), rehydrated-server-
  // finalizing (from IDB after another device already completed the bytes). All three are
  // states where the row may be quietly settled server-side and the local card needs a
  // /sessions/active check to prune it. Excluding processingOnBackend cards was the bug that
  // left a stale "server finalizing" card visible for minutes after a cross-device takeover
  // — the row had already gone terminal on the server, so the fetchActiveSessions detection
  // wasn't polling anymore. See shouldFastPollFromState above for the isolated predicate.
  const shouldFastPoll = shouldFastPollFromState(activeUploads, items);

  useEffect(() => {
    fetchActiveSessions();

    // visibilitychange fires when switching *tabs within the same browser* — but users who
    // Cmd-Tab between two different browsers (e.g. Chrome ↔ Safari for cross-session testing)
    // don't get a visibilitychange in the browser they're switching *to* if that browser
    // never actually hid its tab. `focus` fires on the window regaining focus in that case,
    // so we listen to both.
    const refetchIfVisible = () => {
      if (document.visibilityState === "visible") {
        fetchActiveSessions();
      }
    };
    document.addEventListener("visibilitychange", refetchIfVisible);
    window.addEventListener("focus", refetchIfVisible);

    return () => {
      document.removeEventListener("visibilitychange", refetchIfVisible);
      window.removeEventListener("focus", refetchIfVisible);
    };
  }, [fetchActiveSessions]);

  useEffect(() => {
    console.info("[UPLOAD-DIAG] watcher.shouldFastPoll", { shouldFastPoll, visibility: document.visibilityState });
    if (!shouldFastPoll) return undefined;
    const intervalId = setInterval(() => {
      console.info("[UPLOAD-DIAG] watcher.tick", { visibility: document.visibilityState });
      if (document.visibilityState === "visible") {
        fetchActiveSessions();
      }
    }, FAST_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [shouldFastPoll, fetchActiveSessions]);

  return null;
};

export default CrossDeviceUploadsWatcher;
